import cn from 'classnames'
import { useCallback, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { AbsolutePopup, PopPaper, TPopupVertical } from 'core-components/library/popup'

import { GLOBAL_TOTAL_CATEGORY, OVERALL_CATEGORY } from '../../config'
import { IGlobalCategory } from '../../models/IGlobalCategory'

import styles from './GlobalCategoryOption.module.scss'

interface IGlobalCategoryOption {
  allCategories: IGlobalCategory[]
  category: IGlobalCategory
  categoryIndex: number
  currentCategory?: IGlobalCategory
  onSelectCategory: (selected: IGlobalCategory) => void
}

export const GlobalCategoryOption = ({
  allCategories,
  category,
  categoryIndex,
  currentCategory,
  onSelectCategory,
}: IGlobalCategoryOption) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const displayCategories = useMemo(
    () => allCategories.filter((el) => el.GlobalCategory === category.GlobalCategory),
    [allCategories, category]
  )

  const isSubCategoriesExist = useMemo(
    () => displayCategories.length > 1,
    [displayCategories.length]
  )

  const isCategorySelected = useMemo(
    () => category.GlobalCategory === currentCategory?.GlobalCategory,
    [currentCategory?.GlobalCategory, category.GlobalCategory]
  )

  const getCategoryKey = useCallback((category: IGlobalCategory, subCategory?: IGlobalCategory) => {
    let key = category.GlobalCategory

    if (category.GlobalCategory === subCategory?.GlobalCategory) {
      if (
        !!subCategory?.GlobalSubCategory &&
        subCategory?.GlobalSubCategory !== OVERALL_CATEGORY &&
        subCategory?.GlobalSubCategory !== GLOBAL_TOTAL_CATEGORY
      ) {
        key = subCategory.GlobalSubCategory
      } else {
        key = `${subCategory.GlobalCategory}-total`
      }
    }

    return key
  }, [])

  const selectedCategoryKey = useMemo(() => {
    return getCategoryKey(category, currentCategory)
  }, [category, currentCategory, getCategoryKey])

  const handleExpand = useCallback(() => {
    setIsExpanded((prevState) => !prevState)
  }, [])

  return (
    <div className={styles.button}>
      {!isSubCategoriesExist ? (
        <Button
          key={`jbp-planning-global-switcher-${categoryIndex}-button`}
          className={styles.button}
          color={isCategorySelected ? 'primary' : 'grey'}
          margin={'md'}
          onClick={() => {
            if (onSelectCategory) {
              onSelectCategory(category)
            }
          }}
        >
          <Translation text={category.GlobalCategory} span />
        </Button>
      ) : (
        <>
          {isExpanded && <div className={styles.overlay} onClick={handleExpand} />}
          <AbsolutePopup
            verticalStartPosition={TPopupVertical.Bottom}
            visibilityCallback={() => isExpanded}
            popupContent={() => {
              return (
                <PopPaper className={styles.popupContent}>
                  {displayCategories.map((subCategory, idx) => {
                    const isSubCategorySelected =
                      isCategorySelected &&
                      subCategory.GlobalSubCategory === currentCategory?.GlobalSubCategory

                    const subCategoryKey = getCategoryKey(category, subCategory)

                    return (
                      <Button
                        color={isSubCategorySelected ? 'primary' : 'grey'}
                        className={cn(styles.btn, {
                          [styles.btn__selected]: isSubCategorySelected,
                        })}
                        key={`${subCategory.GlobalCategory}-${subCategory.GlobalSubCategory}-${idx}`}
                        onClick={() => {
                          onSelectCategory(subCategory)
                          setIsExpanded(false)
                        }}
                      >
                        <Translation text={subCategoryKey} span />
                      </Button>
                    )
                  })}
                </PopPaper>
              )
            }}
          >
            <Button
              color={isCategorySelected ? 'primary' : 'grey'}
              className={cn(styles.btn, { [styles.btn__selected]: isCategorySelected })}
              iconPosition="end"
              icon={cn(styles.iconCaret, 'kbase', 'kbase-chevron-bottom')}
              onClick={handleExpand}
            >
              <Translation text={selectedCategoryKey} span />
            </Button>
          </AbsolutePopup>
        </>
      )}
    </div>
  )
}
